/**
 * Create KATA Note Modal Component
 */

import React, { useState } from 'react';
import { X, FileText, Lightbulb, AlertCircle, Target, CheckCircle } from 'lucide-react';
import { useKATABoardStore } from '../../stores/kataBoardStore';

interface CreateNoteModalProps {
  onClose: () => void;
}

const CreateNoteModal: React.FC<CreateNoteModalProps> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    note_type: 'observation',
    title: '',
    content: '',
    is_important: false,
    pdca_step: ''
  });

  const { createNote, loading, currentCycle, selectedStep } = useKATABoardStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentCycle) return;

    const submitData = {
      ...formData,
      kata_cycle: currentCycle.id,
      pdca_step: formData.pdca_step ? parseInt(formData.pdca_step) : undefined
    };

    await createNote(submitData);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const noteTypes = [
    { value: 'observation', label: 'Observation', icon: FileText, color: 'text-gray-600' },
    { value: 'learning', label: 'Learning', icon: Lightbulb, color: 'text-yellow-600' },
    { value: 'obstacle', label: 'Obstacle', icon: AlertCircle, color: 'text-red-600' },
    { value: 'idea', label: 'Idea', icon: Target, color: 'text-blue-600' },
    { value: 'decision', label: 'Decision', icon: CheckCircle, color: 'text-green-600' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Add KATA Note</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Note Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Note Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {noteTypes.map(({ value, label, icon: Icon, color }) => (
                <label
                  key={value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.note_type === value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="note_type"
                    value={value}
                    checked={formData.note_type === value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <Icon className={`h-5 w-5 mr-3 ${color}`} />
                  <span className="text-sm font-medium text-gray-900">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* PDCA Step */}
          {currentCycle && currentCycle.pdca_steps.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related PDCA Step (Optional)
              </label>
              <select
                name="pdca_step"
                value={formData.pdca_step}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a step (or leave blank for cycle-level note)</option>
                {currentCycle.pdca_steps.map((step) => (
                  <option key={step.id} value={step.id}>
                    {step.step_type.toUpperCase()}: {step.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief title for the note..."
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your note content here..."
            />
          </div>

          {/* Important Flag */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_important"
              checked={formData.is_important}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Mark as important
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Add Note
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNoteModal;
